"use client";

import { useEffect, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import { Home, ArrowLeft, RefreshCw } from "lucide-react";
import { captureRuntimeErrorPage } from "@/lib/analytics";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const lastCapturedKey = useRef<string | null>(null);

  useEffect(() => {
    console.error("Application error:", error);
  }, [error]);

  useEffect(() => {
    const key = error.digest ?? `${error.name}:${error.message}`;
    if (lastCapturedKey.current === key) return;
    lastCapturedKey.current = key;
    const search =
      typeof window !== "undefined"
        ? window.location.search.replace(/^\?/, "")
        : "";
    captureRuntimeErrorPage("app", {
      pathname: pathname || "/",
      search,
      error,
    });
  }, [error, pathname]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="space-y-2">
          <Text as="h1" size="2xl" weight="bold" color="default" className="text-5xl">
            Oops!
          </Text>
          <Text as="h2" size="xl" weight="semibold" color="default">
            Something went wrong
          </Text>
          <Text as="p" size="base" weight="normal" color="muted" className="mt-4">
            {error.message || "An unexpected error occurred. Please try again."}
          </Text>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center pt-4">
          <Button
            onClick={reset}
            variant="default"
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </Button>
          <Button
            onClick={() => router.back()}
            variant="secondary"
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </Button>
          <Button
            onClick={() => router.push("/")}
            variant="secondary"
            className="gap-2"
          >
            <Home className="w-4 h-4" />
            Go Home
          </Button>
        </div>
      </div>
    </div>
  );
}

